import { useCallback, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

import { withTranslation } from "react-i18next";

import Snackbar from "@plannel/components/Snackbar";
import ColorUtil from "@plannel/pages/utils/color-util";
import fpSimulationScenarioService from "@plannel/services/fp/fp-simulation-scenario-service";

const { snackBarMessageColor } = ColorUtil;

/** 서버가 플래너 목록을 못 주는 상황(권한·네트워크)에서도 화면이 비지 않도록 두는 최소 후보. */
const FALLBACK_PLANNERS = ["DefaultObpPlanner", "DefaultEbpPlanner"];

/**
 * 시나리오 플래너 선택 팝업 — 구 `ScenarioScriptPopup`을 대체한다(zionex/t3series#5411).
 *
 * <p>계획 실행이 Jython 스크립트가 아니라 Java 플래너 클래스를 가리키게 되면서,
 * "스크립트 파일명 직접 입력"이 "등록된 플래너 중 선택"으로 바뀌었다. 파일명을 손으로 치던
 * 시절에는 오타가 런타임에야 드러났는데, 이제 목록이 서버가 실제로 찾은 것만 담는다.
 *
 * <p>인/아웃바운드 프로시저는 이름과 사용 여부를 따로 둔다 — 사이트에서 이름은 남긴 채
 * 껐다 켜는 운영이 실제로 있다.
 *
 * props: open, value({ plannerNm, inScriptNm, inScriptUseYn, outScriptNm, outScriptUseYn }),
 *        onClose(), onConfirm(value)
 */
const ScenarioPlannerPopup = ({ t, open, value, onClose, onConfirm }) => {
  const [planners, setPlanners] = useState(FALLBACK_PLANNERS);
  const [plannerNm, setPlannerNm] = useState("");
  const [inScriptNm, setInScriptNm] = useState("");
  const [inScriptUseYn, setInScriptUseYn] = useState(false);
  const [outScriptNm, setOutScriptNm] = useState("");
  const [outScriptUseYn, setOutScriptUseYn] = useState(false);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });

  const loadPlanners = useCallback(async () => {
    try {
      const names = await fpSimulationScenarioService.getPlannerNames();
      if (Array.isArray(names) && names.length > 0) {
        setPlanners(names);
      }
    } catch (e) {
      // 목록을 못 받아도 팝업은 떠야 한다. 기본 플래너 둘로 진행한다.
      setPlanners(FALLBACK_PLANNERS);
    }
  }, []);

  useEffect(() => {
    if (!open) return;

    loadPlanners();

    setPlannerNm(value?.plannerNm ?? "");
    setInScriptNm(value?.inScriptNm ?? "");
    setInScriptUseYn(Boolean(value?.inScriptUseYn));
    setOutScriptNm(value?.outScriptNm ?? "");
    setOutScriptUseYn(Boolean(value?.outScriptUseYn));
  }, [open, value, loadPlanners]);

  const handleConfirm = () => {
    if (!plannerNm) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_SELECT_PLANNER") });
      return;
    }

    // 사용으로 켜놓고 이름이 비면 실행 시점에 조용히 건너뛴다. 여기서 막는 편이 낫다.
    const missingName = (inScriptUseYn && inScriptNm.trim() === "") || (outScriptUseYn && outScriptNm.trim() === "");
    if (missingName) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("FP_MSG_ENTER_PROCEDURE") });
      return;
    }

    onConfirm({
      plannerNm,
      inScriptNm: inScriptNm.trim(),
      inScriptUseYn,
      outScriptNm: outScriptNm.trim(),
      outScriptUseYn
    });
  };

  const procedureRow = (label, useYn, setUseYn, name, setName) => (
    <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
      <FormControlLabel
        sx={{ width: 200, mr: 0 }}
        control={<Checkbox size="small" checked={useYn} onChange={event => setUseYn(event.target.checked)} />}
        label={label}
      />
      <TextField
        variant="standard"
        size="small"
        fullWidth
        value={name}
        onChange={event => setName(event.target.value)}
        disabled={!useYn}
        placeholder={`${t("INPUT")} (${t("FP_CASE_SENSITIVE")})`}
      />
    </Box>
  );

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("FP_SELECT_SCENARIO_PLANNER")}</DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          fullWidth
          size="small"
          variant="standard"
          label={t("FP_PLANNER")}
          value={planners.includes(plannerNm) ? plannerNm : ""}
          onChange={event => setPlannerNm(event.target.value)}
        >
          {planners.map(name => (
            <MenuItem key={name} value={name}>{name}</MenuItem>
          ))}
        </TextField>

        {procedureRow(t("FP_INBOUND_PROCEDURE"), inScriptUseYn, setInScriptUseYn, inScriptNm, setInScriptNm)}
        {procedureRow(t("FP_OUTBOUND_PROCEDURE"), outScriptUseYn, setOutScriptUseYn, outScriptNm, setOutScriptNm)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button variant="contained" onClick={handleConfirm}>{t("confirm")}</Button>
      </DialogActions>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </MuiDialog>
  );
};

export default withTranslation()(ScenarioPlannerPopup);
